import styled, { css } from 'styled-components';

/* ───────── Container + header ───────── */

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  margin-bottom: 4px;
  font-family: inherit;

  &:hover {
    color: #006633;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  .titles {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    flex: 1;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  h1 {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    margin: 0;
  }

  .subtitle {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
    max-width: 720px;
    line-height: 1.45;
  }

  .actions {
    display: inline-flex;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

/* ───────── Grid ───────── */

export const EditGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(260px, 1fr);
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .titles h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
  }

  .titles .subtitle {
    display: block;
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
    font-weight: 500;
  }
`;

/* ───────── Status badge ───────── */

type StatusTone = 'success' | 'warning' | 'neutral' | 'info';
const STATUS_MAP: Record<
  StatusTone,
  { bg: string; color: string; border: string }
> = {
  success: { bg: '#e6f3ec', color: '#006633', border: '#c7e4d5' },
  warning: { bg: '#fff4e0', color: '#b45309', border: '#fcd9b0' },
  neutral: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
  info: { bg: '#e0ecff', color: '#1d4ed8', border: '#c7ddff' },
};

export const StatusBadge = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  ${({ $tone }) => css`
    background: ${STATUS_MAP[$tone].bg};
    color: ${STATUS_MAP[$tone].color};
    border: 1px solid ${STATUS_MAP[$tone].border};
  `}

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

/* ───────── Buttons ───────── */

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
const BTN_MAP: Record<
  BtnVariant,
  {
    bg: string;
    color: string;
    border: string;
    hoverBg: string;
    hoverBorder?: string;
  }
> = {
  primary: {
    bg: '#006633',
    color: '#ffffff',
    border: '#006633',
    hoverBg: '#00451f',
  },
  secondary: {
    bg: '#ffffff',
    color: '#0f172a',
    border: '#e2e8f0',
    hoverBg: '#f8fafc',
    hoverBorder: '#006633',
  },
  ghost: {
    bg: 'transparent',
    color: '#64748b',
    border: 'transparent',
    hoverBg: '#f1f5f9',
  },
  danger: {
    bg: '#ffffff',
    color: '#b91c1c',
    border: '#fecaca',
    hoverBg: '#fef2f2',
  },
};

export const Button = styled.button<{
  $variant?: BtnVariant;
  $size?: 'sm' | 'md';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: ${({ $size }) => ($size === 'sm' ? '30px' : '36px')};
  padding: 0 ${({ $size }) => ($size === 'sm' ? '12px' : '14px')};
  border-radius: ${({ $size }) => ($size === 'sm' ? '8px' : '10px')};
  font-size: ${({ $size }) => ($size === 'sm' ? '12px' : '13px')};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  ${({ $variant = 'primary' }) => css`
    background: ${BTN_MAP[$variant].bg};
    color: ${BTN_MAP[$variant].color};
    border: 1px solid ${BTN_MAP[$variant].border};

    &:hover:not(:disabled) {
      background: ${BTN_MAP[$variant].hoverBg};
      ${BTN_MAP[$variant].hoverBorder &&
      css`
        border-color: ${BTN_MAP[$variant].hoverBorder};
        color: #006633;
      `}
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ───────── Questions ───────── */

export const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const QuestionCard = styled.div`
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c7e4d5;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
  }

  .question-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
  }

  .index-badge {
    min-width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #e6f3ec;
    color: #006633;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }

  .question-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .question-text {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
    line-height: 1.4;
  }

  .question-meta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .question-actions {
    display: inline-flex;
    gap: 6px;
    flex-shrink: 0;
  }
`;

export const AnswerList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 12px 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const AnswerItem = styled.li<{ $variant: 'radio' | 'checkbox' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #fafbfc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  font-size: 13px;
  color: #334155;
  font-weight: 500;

  .marker {
    width: 16px;
    height: 16px;
    border: 2px solid #cbd5e1;
    border-radius: ${({ $variant }) => ($variant === 'radio' ? '50%' : '4px')};
    flex-shrink: 0;
  }
`;

export const QuestionEmptyState = styled.div`
  padding: 36px 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 500;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
  background: #fafbfc;

  .icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #f1f5f9;
    color: #94a3b8;
    margin: 0 auto 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  strong {
    display: block;
    color: #0f172a;
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 4px;
  }
`;

/* ───────── Icon action button ───────── */

export const IconAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    ${({ $danger }) =>
      $danger
        ? css`
            background: #fef2f2;
            border-color: #fecaca;
            color: #b91c1c;
          `
        : css`
            background: #f1faf4;
            border-color: #c7e4d5;
            color: #006633;
          `}
  }
`;

/* ───────── Sidebar info ───────── */

export const InfoList = styled.dl`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const InfoRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;

  .icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: #f1faf4;
    color: #006633;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  dt {
    font-size: 10.5px;
    color: #64748b;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin: 0;
  }

  dd {
    font-size: 13px;
    color: #0f172a;
    font-weight: 600;
    margin: 0;
    line-height: 1.4;
    word-break: break-word;
  }

  dd.muted {
    color: #64748b;
    font-weight: 500;
    font-style: italic;
  }
`;

export const SidebarFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
`;
